"use client";

/**
 * Landing Page - Hero Section Component
 */

import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { cn } from "@/shared/core/utils";
import { Input } from "@/shared/presentation/components/ui/input";
import { Button } from "@/shared/presentation/components/ui/button";
import { Spinner } from "@/shared/presentation/components/custom";
import {
  useSubscribeToMailingList,
  landingValidation,
} from "../../domain/hooks";
import { LANDING_IMAGES } from "../../constants";

interface HeroSectionProps {
  checkCaptcha: () => Promise<string | null>;
}

export function HeroSection({ checkCaptcha }: HeroSectionProps) {
  const t = useTranslations("landing");
  const [email, setEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);

  const {
    mutateAsync: subscribe,
    isPending: isLoading,
    isSuccess: isEmailSent,
  } = useSubscribeToMailingList();

  const updateEmail = (input: string) => {
    setIsEmailValid(landingValidation.isEmailValid(input));
    setEmail(input);
  };

  const sendEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const recaptchaToken = await checkCaptcha();

    if (recaptchaToken === null) {
      return;
    }

    await subscribe({
      email,
      captchaToken: recaptchaToken,
    });
  };

  return (
    <header className="relative h-auto w-full max-w-full overflow-hidden text-center">
      {/* Background Image */}
      <Image
        src={LANDING_IMAGES.backgroundTractor}
        alt="Farm background"
        fill
        priority
        className="object-cover object-center"
        sizes="100vw"
      />

      {/* Overlay Content */}
      <div className="relative h-auto w-full max-w-full bg-primary/60 px-0 py-6 text-center">
        <div className="my-[60px] flex flex-col">
          {/* Hero Title */}
          <h2 className="mx-auto my-9 max-w-5xl px-4 text-[50px] font-bold leading-[75px] tracking-[1px] text-white">
            {t("hero.title")}
          </h2>

          {/* Subtitle */}
          <p className="mx-auto my-9 max-w-5xl rounded-xl bg-primary/60 px-4 text-lg leading-8 text-white">
            {t("hero.subtitle")}
          </p>

          {/* Email Subscription Form */}
          <form
            onSubmit={sendEmail}
            className="mx-auto mb-6 mt-4 flex w-full max-w-5xl flex-col items-center justify-between gap-2 rounded-[30px] bg-transparent p-2 md:w-1/2 lg:flex-row lg:bg-background"
          >
            <Input
              className="h-11 min-w-0 flex-1 rounded-[30px] border border-solid border-white bg-background px-4 py-2 text-left text-base text-primary-dark outline-none"
              type="email"
              placeholder={t("hero.emailPlaceholder")}
              onChange={(e) => updateEmail(e.target.value)}
              value={email}
            />

            {isEmailSent ? (
              <div className="ml-2 mt-2 text-nowrap rounded-[30px] bg-green-200 px-4 py-2 text-center text-base uppercase tracking-[1px] text-green-700 transition-all md:ml-0 md:mt-0">
                {t("hero.emailSuccess")}
              </div>
            ) : (
              <Button
                type="submit"
                disabled={!isEmailValid || isLoading}
                className={cn(
                  "ml-2 mt-2 text-nowrap rounded-[30px] bg-primary px-4 py-2 text-center text-base uppercase tracking-[1px] text-white transition-all hover:bg-opacity-80 md:ml-0 md:mt-0",
                  {
                    "cursor-not-allowed bg-muted": !isEmailValid,
                    "cursor-pointer": isEmailValid,
                  }
                )}
              >
                {isLoading ? (
                  <Spinner size="md" variant="white" />
                ) : (
                  t("hero.learnMore")
                )}
              </Button>
            )}
          </form>
        </div>
      </div>
    </header>
  );
}

export default HeroSection;
